import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { combineLatest, filter, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DashboardTitleService } from '../../shared-services/dashboard-title.service';
import { PublishedDashboardToolbarService, PublishedDashboardToolbarState, PublishedDashboardAction } from '../../shared-services/published-dashboard-toolbar.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ThemeService } from '../../shared-services/theme.service';
import { LayoutService } from '../../shared-services/layout.service';
import { SlideshowService } from '../../shared-services/slideshow.service';
import { SelfServiceDashboardService } from '../../self-service-visualization/shared/self-service-dashboard.service';
import { readJson, writeDynamicDashboardCache, purgeLegacyDynamicDashboardCaches } from '../../self-service-visualization/shared/self-service-storage.utils';
import { openUserGuide } from '../../utils/user-guide.utils';
import { FloatingChatWidgetComponent } from '../../global-components/floating-chat-widget/floating-chat-widget.component';

interface TopNavigationItem {
  label: string;
  description?: string;
  icon?: string;
  path: string;
  image?: string;
  accent?: string;
}

interface TopNavigationSectionConfig {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  source: 'static' | 'menuGroup';
  groupName?: string;
  /** Panel presentation. 'cards' (default) is the thumbnail grid used by the
   *  builder screens, which have real preview art. 'list' is the compact row
   *  list used for published dashboards, which have none — a grid of identical
   *  placeholder thumbnails carried no information. */
  layout?: 'cards' | 'list';
  thumbnailPattern?: string;
  fallbackThumbnail?: string;
  items?: TopNavigationItem[];
}

interface TopNavigationSection extends TopNavigationSectionConfig {
  resolvedItems: TopNavigationItem[];
}

@Component({
  selector: 'app-side-navbar',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule,
    RouterModule,
    FloatingChatWidgetComponent,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrl: './side-navbar.component.scss',
})
export class SideNavbarComponent implements OnDestroy {
  shellLauncherOpen = false;
  readonly shellLauncherItems = [
    { view: 'data', label: 'Data Builder', icon: 'storage' },
    { view: 'dashboards', label: 'Dashboard Builder', icon: 'dashboard_customize' },
    { view: 'layout', label: 'DVM Layout Builder', icon: 'view_quilt' },
  ];
  /** Sidebar drag-resize bounds + persistence key. Min/max chosen so the
   *  menu icons + labels stay readable at min and don't crowd content at max. */
  private static readonly SIDEBAR_MIN_WIDTH = 200;
  private static readonly SIDEBAR_MAX_WIDTH = 480;
  private static readonly SIDEBAR_DEFAULT_WIDTH = 280;
  private static readonly SIDEBAR_WIDTH_STORAGE_KEY = 'dvm.sidebar.expandedWidth';

  isExpanded = true;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  isCollapsed = true;
  isMobileLayout = false;
  isMobileDrawerOpen = false;
  username!: string;
  /** Current user's email, resolved from the Keycloak account profile
   *  (loadUserProfile) rather than the cached JWT `email` claim, which can go
   *  stale if the user's email is changed in Keycloak after the token was issued. */
  private userEmail = '';
  logo!: string;
  companyText = environment.COMPANY_LOGO_TEXT;
  /** Project-mode top-toolbar background, exposed to the SCSS as
   *  `--environment-header-color`. Non-project mode deliberately returns null
   *  so the normal theme/floating-toolbar background remains in control.
   *
   *  OPT-IN: null unless a deployment sets `HEADER_COLOR`. It used to read
   *  `SECONDARY_COLOR` (which nothing else consumes and which every env file
   *  ships as '#9ccafb'), so the toolbar was force-tinted light blue everywhere
   *  — including the builder's floating toolbar, whose design is near-white.
   *
   *  In project mode, HEADER_COLOR wins and SECONDARY_COLOR is the fallback.
   *  This keeps the project header and project navigation on the same
   *  deployment colour while still allowing a dedicated header override.
   *
   *  Returns null (not '') when unset: Angular REMOVES a null style binding, so
   *  the SCSS `var(--environment-header-color, <default>)` falls back properly.
   *  An empty string would define the custom property as empty, which makes
   *  `var()` resolve to nothing and drops the background declaration entirely. */
  readonly topHeaderColor: string | null = (() => {
    const env = environment as Record<string, unknown>;
    const projectMode = env['PROJECT_MODE'] === true || env['PROJECT_MODE'] === 'true';
    if (!projectMode) return null;
    const configuredHeader = String(env['HEADER_COLOR'] ?? '').trim();
    const projectFallback = String(env['SECONDARY_COLOR'] ?? '').trim();
    return configuredHeader || projectFallback || null;
  })();
  readonly sidenavColor: string | null =
    String((environment as Record<string, unknown>)['SECONDARY_COLOR'] ?? '').trim() || null;
  secondaryLogo!: string;
  clientLogo: string = '';
  clientIcon: string = '';
  clientText: string = '';
  hasClientLogo: boolean = false;
  useAltSidenav: boolean = false;

  /** Width (px) used when the sidebar is expanded and not in mobile drawer
   *  mode. Hydrated from localStorage on init; updated live during drag. */
  sidebarExpandedWidth = SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;
  /** True while the user is actively dragging the resize handle. Suppresses
   *  the sidebar's width transition so width tracks the cursor 1:1. */
  isResizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizePointerId: number | null = null;

  // Light/Dark theme toggle
  private themeService = inject(ThemeService);
  private layoutService = inject(LayoutService);
  private slideshowService = inject(SlideshowService);
  private selfServiceDashboardService = inject(SelfServiceDashboardService);
  private publishedToolbar = inject(PublishedDashboardToolbarService);

  /** Live state of the published dashboard's settings gear (Edit Layout / Reset
   *  / Back / Share), pushed up from DvmPublishedDashboardComponent so the gear
   *  can live here in the top toolbar instead of a floating in-page bar. */
  publishedToolbarState: PublishedDashboardToolbarState = { active: false, editMode: false, hasWidgets: false, showOwnerActions: false };
  private publishedToolbarSub?: Subscription;

  /** Fire a published-dashboard settings action back to that component. */
  triggerPublishedAction(action: PublishedDashboardAction): void {
    this.publishedToolbar.trigger(action);
  }
  private themeSub?: Subscription;
  private pendingPreviewSub?: Subscription;
  private slideshowStateSub?: Subscription;
  private slideshowProgressSub?: Subscription;
  private dashboardTitleSub?: Subscription;
  isDarkTheme = false;
  dashboardTitle = '';
  dashboardTitlePosition: 'top' | 'below' | 'default' = 'default';
  dashboardTitleFontSize = '';
  showThemeOptions = true;
  showThemeToggle = true;
  /** Whether the Make-in-India/secondary logo is shown (env SECONDARY_LOGO_ENABLED). */
  showSecondaryLogo = true;
  /** Whether the floating chat-assistant button is mounted (env CHAT_ENABLED).
   *  Unset = shown, matching the other *_ENABLED flags. */
  showChatAssistant = true;

  /** Drives the toolbar "Save Theme" button visibility — true after the user
   * hits Apply & Preview in the Theme Options dialog and not yet committed. */
  hasPendingThemePreview = false;

  /** Mirrored slideshow state — toolbar template binds to these for the
   *  inline Play/Pause/Apply/Save/Stop controls. Plain fields rather than
   *  async pipes so multiple template bindings on the same state field
   *  don't open separate subscriptions. */
  slideshowActive = false;
  slideshowPlaying = false;
  slideshowCurrentName = '';
  slideshowIndex = 0;
  slideshowTotal = 0;

  /** 0–1 progress through the current slide cycle. Bound to the progress
   *  bar's width %; updated each animation frame while playing. */
  slideshowProgress = 0;

  /** Live ref to the Theme Options dialog so re-clicking the toolbar button
   * while the dialog is already open is a no-op instead of stacking dialogs. */
  private themeSelectorDialogRef: MatDialogRef<ThemeSelectorComponent> | null = null;

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /** Persist the previewed theme + customs + layout from the toolbar Save
   *  button. The single Save covers all three pending states so the user
   *  doesn't have to click multiple buttons after previewing changes. */
  saveThemePreview(): void {
    this.themeService.commitPendingPreview();
    this.layoutService.commitPendingLayout();
  }

  // ── Slideshow toolbar actions (delegate to SlideshowService) ─────

  /** Toggle the autoplay timer; the dashboard keeps showing the current
   *  slide either way. */
  toggleSlideshowPlayback(): void {
    if (this.slideshowPlaying) {
      this.slideshowService.pause();
    } else {
      this.slideshowService.play();
    }
  }

  slideshowNext(): void {
    this.slideshowService.next();
  }

  slideshowPrev(): void {
    this.slideshowService.prev();
  }

  /** "Apply & Preview" from the toolbar — keep the current slide as a
   *  pending preview (Save Theme button picks it up), end the slideshow. */
  slideshowApply(): void {
    this.slideshowService.applyAndStop();
  }

  /** Commit current slide to localStorage immediately + end. */
  slideshowSave(): void {
    this.slideshowService.saveAndStop();
  }

  /** Stop and revert to the theme that was active before Play was hit. */
  slideshowStop(): void {
    this.slideshowService.cancelAndStop();
  }

  private onDashboardPublished = () => {
    // Show the just-published dashboard AT ONCE from the builder's localStorage
    // saves — the backend's `dynamic_filter` re-query below can race the indexing
    // of the brand-new row, so without this the dashboard would be missing (and
    // then vanish again a few seconds later when the racing response lands). This
    // localStorage merge covers that window; the API loader reconciles once the
    // row is indexed (dedup by slug keeps it single).
    this.mergeLocalSavedDashboards();
    // Then re-query the email-filtered loader (authoritative, logout-proof).
    this.loadDynamicDashboard();
  };

  /** Build sidebar "Dashboards" group entries from the builder's localStorage
   *  saves (`dvm.selfService.savedDashboards`) — used at publish time so a new
   *  dashboard appears at once even before the backend lists it. The route slug
   *  MUST be derived from the dashboard_id (config.dashboardId), which carries
   *  the uniqueness suffix, so it matches the `/dvm-published/<slug>` the
   *  published view resolves (and the API loader's slug), keeping dedup correct. */
  private mergeLocalSavedDashboards(): void {
    let dashboards: any[] = [];
    try {
      dashboards = readJson<any[]>(SideNavbarComponent.SAVED_DASHBOARDS_STORAGE_KEY, []) || [];
    } catch {
      return;
    }
    if (!Array.isArray(dashboards) || !dashboards.length) return;

    const groupItems = dashboards
      .map((d: any) => {
        // ONLY PUBLISHED dashboards have `config.dashboardId` — that IS the URL
        // slug the published view resolves. Drafts have only the internal `d.id`
        // (`dvm-dashboard-<ts>-<rand>`), which is NOT a published slug — linking
        // to it gives "No published DVM dashboard found". So skip anything
        // without a real dashboardId (and never fall back to d.id).
        const dashboardId = String(d?.config?.dashboardId ?? '').trim();
        const isPublished = d?.status === 'published' || !!dashboardId;
        if (!dashboardId || !isPublished) return null;
        const slug = this.nameToSlug(dashboardId);
        if (!slug) return null;
        // Same ownership registration the API loader does, so the creator keeps
        // the full sidebar (not the trimmed shared view) on their new dashboard.
        this.ownedDashboardSlugs.add(slug);
        this.selfServiceDashboardService.registerOwnedDashboardSlug(slug);
        const isLandingPage = String(d?.config?.['dashboardType'] ?? '') === 'landing-page';
        return { name: String(d?.name ?? dashboardId), icon: isLandingPage ? 'home' : 'dashboard', path: `/dvm-published/${slug}`, ...(isLandingPage ? { isLandingPage: true } : {}) };
      })
      .filter((x: any): x is { name: string; icon: string; path: string; isLandingPage?: boolean } => !!x);

    if (groupItems.length) {
      this.displayedMenuCache = undefined;
      this.applyPublishedDashboards(groupItems);
    }
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.pendingPreviewSub?.unsubscribe();
    this.slideshowStateSub?.unsubscribe();
    this.slideshowProgressSub?.unsubscribe();
    this.dashboardTitleSub?.unsubscribe();
    if (this.topNavigationCloseTimer) clearTimeout(this.topNavigationCloseTimer);
    window.removeEventListener('dvm:dashboard-published', this.onDashboardPublished);
    // If the user navigates away mid-drag the window listeners would leak
    // and keep firing on a destroyed component — clean them up here.
    if (this.isResizingSidebar) this.endSidebarResize(false);
  }

  /** Begin a drag-resize on the sidebar's right edge. No-op on mobile (the
   *  drawer is full-width) or when collapsed (handle isn't rendered). */
  startSidebarResize(event: PointerEvent): void {
    if (this.isMobileLayout || this.isCollapsed) return;
    event.preventDefault();
    event.stopPropagation();
    this.resizePointerId = event.pointerId;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarExpandedWidth;
    this.isResizingSidebar = true;
    window.addEventListener('pointermove', this.onResizePointerMove);
    window.addEventListener('pointerup', this.onResizePointerUp);
    window.addEventListener('pointercancel', this.onResizePointerUp);
  }

  private onResizePointerMove = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    const delta = event.clientX - this.resizeStartX;
    const next = this.clampSidebarWidth(this.resizeStartWidth + delta);
    if (next !== this.sidebarExpandedWidth) {
      this.sidebarExpandedWidth = next;
    }
  };

  private onResizePointerUp = (event: PointerEvent): void => {
    if (this.resizePointerId !== event.pointerId) return;
    this.endSidebarResize(true);
  };

  /** Tear down the drag — optionally persist the new width and trigger the
   *  same staged reflow used by collapse/expand so chart widgets resize. */
  protected endSidebarResize(commit: boolean): void {
    this.isResizingSidebar = false;
    this.resizePointerId = null;
    window.removeEventListener('pointermove', this.onResizePointerMove);
    window.removeEventListener('pointerup', this.onResizePointerUp);
    window.removeEventListener('pointercancel', this.onResizePointerUp);
    if (commit) {
      this.persistSidebarWidth();
      this.scheduleDesktopReflow();
    }
  }

  protected clampSidebarWidth(width: number): number {
    return Math.min(
      SideNavbarComponent.SIDEBAR_MAX_WIDTH,
      Math.max(SideNavbarComponent.SIDEBAR_MIN_WIDTH, Math.round(width)),
    );
  }

  protected persistSidebarWidth(): void {
    try {
      localStorage.setItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
        String(this.sidebarExpandedWidth),
      );
    } catch {
      /* ignore */
    }
  }

  protected loadSidebarWidth(): void {
    try {
      const raw = localStorage.getItem(
        SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY,
      );
      if (!raw) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) return;
      this.sidebarExpandedWidth = this.clampSidebarWidth(parsed);
    } catch {
      /* ignore */
    }
  }

  /**
   * Select logos based on environment flags.
   * Use Case 1: CLIENT_LOGO_SRC empty → show default SIM Advisory branding (logo + company text).
   * Use Case 2: CLIENT_LOGO_SRC provided → show client logo alone.
   * Colors (toolbar/sidebar bg + text) are controlled by ThemeService via CSS variables.
   */
  protected initLogos(): void {
    const env = environment as any;
    const useAlt = env.USE_ALT_SIDENAV === true || env.USE_ALT_SIDENAV === 'true';
    this.useAltSidenav = useAlt;
    this.showThemeOptions = this.isEnabled(env.THEME_OPTIONS_ENABLED);
    this.showThemeToggle = this.isEnabled(env.THEME_TOGGLE_ENABLED);
    this.showSecondaryLogo = this.isEnabled(env.SECONDARY_LOGO_ENABLED);
    this.showChatAssistant = this.isEnabled(env.CHAT_ENABLED);
    this.projectMode = env.PROJECT_MODE === true || env.PROJECT_MODE === 'true';

    const clientSrc = (env.CLIENT_LOGO_SRC || '').trim();
    const clientIcon = (env.CLIENT_LOGO_ICON || '').trim();
    const clientText = (env.CLIENT_LOGO_TEXT || '').trim();
    this.clientLogo = clientSrc;
    this.clientIcon = clientIcon;
    this.clientText = clientText;
    this.hasClientLogo = !!clientSrc || !!clientIcon;

    if (useAlt) {
      this.logo = env.ALT_COMPANY_LOGO_SRC;
      this.secondaryLogo = env.ALT_SECONDARY_LOGO_SRC;
    } else {
      // Prefer DEFAULT_COMPANY_LOGO (local SVG) over COMPANY_LOGO_SRC (remote PNG) when set.
      // Project mode keeps the themed BLUE toolbar (the other modes get the
      // light floating one), and the default logo's wordmark is navy on blue.
      // Use the white artwork there instead: DEFAULT_COMPANY_LOGO_ON_DARK when
      // configured, otherwise the shipped `-white` sibling of the default.
      const defaultLogo = (env.DEFAULT_COMPANY_LOGO || '').trim();
      const darkBgLogo = (env.DEFAULT_COMPANY_LOGO_ON_DARK || '').trim();
      this.logo = this.projectMode
        ? darkBgLogo || this.whiteLogoVariant(defaultLogo) || environment.COMPANY_LOGO_SRC
        : defaultLogo || environment.COMPANY_LOGO_SRC;
      // Prefer DEFAULT_MAKE_INDIA_LOGO (local SVG) over SECONDARY_LOGO_SRC (remote PNG) when set.
      const defaultMakeIndia = (env.DEFAULT_MAKE_INDIA_LOGO || '').trim();
      this.secondaryLogo = defaultMakeIndia || environment.SECONDARY_LOGO_SRC;
    }
  }

  /** `assets/foo.svg` → `assets/foo-white.svg`. Returns '' when there's nothing
   *  to derive from, so the caller falls back to the remote logo. */
  private whiteLogoVariant(path: string): string {
    const source = (path || '').trim();
    if (!source) return '';
    if (/-white\.svg$/i.test(source)) return source;
    return source.replace(/\.svg$/i, '-white.svg');
  }

  private isEnabled(value: boolean | string | undefined): boolean {
    if (value === undefined || value === null || value === '') {
      return true;
    }
    return value === true || String(value).trim().toLowerCase() === 'true';
  }

  /** Project-mode branch: only the published
   *  Dashboards list should show in the sidenav — no Visualization Builder entry,
   *  no floating builder-screens launcher. Opt-in (missing/false = full menu),
   *  unlike `isEnabled` above which treats an unset flag as enabled — a brand
   *  new flag must default OFF so every other environment is unaffected. */
  protected projectMode = false;

  menuItems: any[] = [];
  topNavigationSections: TopNavigationSection[] = [];
  activeTopNavigationId = '';
  private topNavigationConfig: TopNavigationSectionConfig[] = [];
  private topNavigationCloseTimer?: ReturnType<typeof setTimeout>;
  expandedMenus: Set<string> = new Set();
  isChatRoute: boolean = false;
  // Macro panel state
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macros: string[] = [];
  macroErrorMessage: string | null = null;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
    private responsiveLayoutService: ResponsiveLayoutService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dashboardTitleService: DashboardTitleService,
  ) {
    (window as unknown as Record<string, unknown>)['DVM_ORBIT_DEBUG_V1'] = () => this.logShellLauncherDiagnostics();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isChatRoute = event.urlAfterRedirects.includes('/chat');
        this.currentUrl = event.urlAfterRedirects || '';
        this.rebuildTopNavigationSections();
        if (this.isMobileLayout) {
          this.isMobileDrawerOpen = false;
        }
      });
  }

  openBuilderScreen(view: string): void {
    this.shellLauncherOpen = false;
    this.router.navigate(['/generate-visualization'], { queryParams: { view } });
  }

  private logShellLauncherDiagnostics(): Record<string, unknown> {
    const launcher = document.querySelector<HTMLElement>('.shell-screen-launcher');
    const rect = launcher?.getBoundingClientRect();
    const style = launcher ? getComputedStyle(launcher) : null;
    const diagnostic = {
      source: 'persistent-side-navbar-shell',
      route: this.currentUrl || this.router.url,
      isDashboardRoute: this.isDashboardRoute,
      isDvmLayoutBuilderRoute: this.isDvmLayoutBuilderRoute,
      isSelfServiceRoute: this.isSelfServiceRoute,
      launcherFound: !!launcher,
      launcherRect: rect?.toJSON(),
      inViewport: !!rect && rect.right > 0 && rect.bottom > 0 && rect.left < innerWidth && rect.top < innerHeight,
      computed: style ? { display: style.display, visibility: style.visibility, opacity: style.opacity, zIndex: style.zIndex, position: style.position } : null,
      viewport: { width: innerWidth, height: innerHeight },
    };
    console.log('[DVM_ORBIT_DEBUG_V1]', diagnostic);
    return diagnostic;
  }

  /** Latest navigated URL — drives the shared-view menu lockdown. */
  currentUrl = '';

  get isSelfServiceRoute(): boolean {
    return (this.currentUrl || this.router.url || '').includes('/generate-visualization');
  }

  get isDataBuilderRoute(): boolean {
    if (!this.isSelfServiceRoute) return false;
    const url = this.currentUrl || this.router.url || '';
    const view = this.router.parseUrl(url).queryParams['view'];
    return !view || view === 'data';
  }

  get isDashboardRoute(): boolean {
    const url = this.currentUrl || this.router.url || '';
    return /\/(?:dashboard|dynamic-dashboard|dvm-published)(?:\/|\?|$)/.test(url);
  }

  get isDvmLayoutBuilderRoute(): boolean {
    const url = this.currentUrl || this.router.url || '';
    return /\/dvm-layout-builder(?:\/|\?|$)/.test(url);
  }

  protected readonly projectRailOffsetPx = 104;

  get showProjectModeRail(): boolean {
    return this.projectMode && !this.isMobileLayout;
  }

  get projectModeDashboardItems(): Array<{ label: string; path: string; active: boolean }> {
    const dashboardsGroup = this.displayedMenuItems.find(
      (item) => item?.name === 'Dashboards' && Array.isArray(item?.children),
    );
    const children = Array.isArray(dashboardsGroup?.children) ? dashboardsGroup.children : [];
    return children.filter((item: any) => !!item?.path).map((item: any) => ({
      label: String(item?.name || 'Dashboard'),
      path: String(item?.path),
      active: this.isTopNavigationItemActive(String(item?.path)),
    }));
  }

  get projectModeBrandMonogram(): string {
    const words = String(this.companyText || '').trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map((word) => word[0]?.toUpperCase() || '').join('') || 'SI';
  }

  triggerSelfServiceAction(action: 'whats-new' | 'help' | 'chat'): void {
    window.dispatchEvent(new CustomEvent('dvm:self-service-action', { detail: action }));
  }

  /** Open the User Guide from the global toolbar. Calls the shared helper
   *  DIRECTLY rather than going through `triggerSelfServiceAction('help')` —
   *  that dispatches a window event only the self-service shell listens for, so
   *  it would silently do nothing on every other route. */
  openGuide(): void {
    openUserGuide();
  }

  /** Slugs of dashboards the CURRENT user owns, sourced from the email-filtered
   *  `dynamic_filter` API in loadDynamicDashboard(). Used to keep the full
   *  sidebar (no lockdown) when the owner opens their own dashboard. Held in
   *  memory rather than read from localStorage so it survives logout — logout
   *  runs localStorage.clear(), but this is re-populated from the API on the
   *  next login. */
  private ownedDashboardSlugs = new Set<string>();

  /** When a shared read-only dashboard is open (`/dynamic-dashboard/:slug`,
   *  reached via a Share link) BY SOMEONE OTHER THAN THE CREATOR, the sidebar is
   *  trimmed to a minimal, recipient-appropriate nav: the **Visualization
   *  Builder** entry (so the recipient can start their own work) PLUS the one
   *  **shared dashboard** item — not the whole app's navigation, and not just a
   *  single locked item. If the shared dashboard isn't already in menuItems (a
   *  recipient who never had it in their own list), we synthesize an entry from
   *  the slug so it still shows.
   *
   *  IMPORTANT: the creator viewing their OWN dashboard uses the same
   *  `/dynamic-dashboard/:slug` route (that's how their published dashboards are
   *  listed in the sidebar), so we must NOT trim for them — they get the full
   *  nav. `sharedDashboardSlug()` returns '' for a dashboard the current user
   *  owns, and the full menu shows. Any other route also returns the full menu
   *  unchanged. */
  // Memo so the getter returns a stable array reference across change-detection
  // cycles (the *ngFor has no trackBy) — recomputed only when the slug or the
  // underlying menuItems array reference changes.
  private displayedMenuCache?: { slug: string; source: any[]; result: any[] };

  get displayedMenuItems(): any[] {
    const slug = this.sharedDashboardSlug();
    if (
      this.displayedMenuCache &&
      this.displayedMenuCache.slug === slug &&
      this.displayedMenuCache.source === this.menuItems
    ) {
      return this.displayedMenuCache.result;
    }

    let result: any[];
    if (!slug) {
      result = this.menuItems;
    } else {
      const path = `/dvm-published/${slug}`;

      // Keep the Visualization Builder entry (matched by its route, so a
      // renamed label still works) so the recipient can start their own work.
      const builderItems = this.menuItems.filter(
        (item) => item?.path === '/generate-visualization',
      );

      // The shared dashboard item — reuse the existing menu item when present,
      // otherwise synthesize one from the slug.
      const existing = this.menuItems.find((item) => item?.path === path);
      const sharedItem =
        existing ?? {
          name: slug
            .replace(/[-_]+/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
          icon: 'dashboard',
          path,
        };

      result = [...builderItems, sharedItem];
    }

    if (this.projectMode) {
      // Project-mode branch: only the published Dashboards group belongs in
      // the sidenav — drop the Visualization Builder entry (its route/screens
      // are still reachable directly by URL, just not surfaced in the menu).
      result = result.filter((item) => item?.path !== '/generate-visualization');
    }

    this.displayedMenuCache = { slug, source: this.menuItems, result };
    return result;
  }

  /** The slug of the dynamic-dashboard route WHEN THE TRIMMED SHARED-VIEW NAV
   *  should apply, or '' when it shouldn't. It applies when:
   *   - the URL carries `?shared=1` (the Share link / preview) — ALWAYS, even for
   *     the creator, so the preview matches the recipient experience; OR
   *   - the current user is NOT the dashboard's creator (a genuine recipient).
   *  It does NOT apply for the creator opening their own dashboard from the
   *  sidebar (no `shared` flag) — they keep the full nav. */
  private sharedDashboardSlug(): string {
    const url = this.currentUrl || this.router.url || '';
    const match = url.match(/\/(?:dynamic-dashboard|dvm-published)\/([^/?#]+)/);
    const slug = match ? decodeURIComponent(match[1]) : '';
    if (!slug) return '';
    // Explicit share/preview link → always trimmed, regardless of ownership.
    if (/[?&]shared=1(?:&|$)/.test(url)) return slug;
    // Internal table→dashboard navigation (carries a `filterKey` query param) is
    // NOT a share link — it's the user jumping between their own dashboards. Keep
    // the full sidebar so the Published Dashboards group doesn't vanish. (Share
    // links are the only thing that should trim for a non-owner; a `filterKey`
    // jump never is.)
    if (/[?&]filterKey=/.test(url)) return '';
    // Otherwise trim only for a non-owner (genuine recipient). The creator
    // viewing their own dashboard keeps the full menu.
    return this.ownsDashboardSlug(slug) ? '' : slug;
  }

  /** True when the current user is the creator of the dashboard with this slug.
   *  Two independent signals, either sufficient:
   *   1) `ownedDashboardSlugs` — the email-filtered `dynamic_filter` API result
   *      (loadDynamicDashboard). Authoritative and LOGOUT-PROOF (re-fetched on
   *      every login), so it covers "log out, log back in, open my dashboard".
   *   2) `dvm.selfService.savedDashboards` — the builder's local saves. Covers
   *      the same session BEFORE the API responds, and offline/dev with no API.
   *  (The per-user dynamic cache `dvm.dynamicDashboard.<slug>` is deliberately
   *  NOT used: `DashboardComponent.loadDynamicApiDashboard` writes it for a
   *  recipient too when they open a share link, so it can't tell owner from
   *  recipient.) */
  private ownsDashboardSlug(slug: string): boolean {
    const target = (slug || '').toLowerCase();
    if (!target) return false;
    if (this.ownedDashboardSlugs.has(target)) return true;
    try {
      // savedDashboards is stored compressed by the builder; readJson
      // decompresses it. A raw JSON.parse would throw on the compressed string.
      const dashboards = readJson<any[]>(
        SideNavbarComponent.SAVED_DASHBOARDS_STORAGE_KEY,
        [],
      );
      return (
        Array.isArray(dashboards) &&
        dashboards.some((d: any) => {
          // The route slug is nameToSlug(dashboard_id), and dashboard_id carries
          // a uniqueness SUFFIX (e.g. "my-dashboard-abc123"). Matching only on
          // nameToSlug(d.name) ("my-dashboard") therefore MISSED a freshly
          // published dashboard whose owned-slug hadn't hit the API list yet —
          // so the creator was wrongly treated as a recipient and shown the
          // trimmed single-dashboard view instead of the full sidebar list.
          // Match the persisted dashboard_id slug too (config.dashboardId / id).
          const nameSlug = this.nameToSlug(String(d?.name ?? ''));
          const idSlug = this.nameToSlug(String(d?.config?.dashboardId ?? d?.id ?? ''));
          return nameSlug === target || (!!idSlug && idSlug === target);
        })
      );
    } catch {
      return false;
    }
  }


  async ngOnInit(): Promise<void> {
    this.currentUrl = this.router.url || '';
    this.breakpointObserver.observe(['(max-width: 991.98px)']).subscribe((state) => {
      this.isMobileLayout = state.matches;
      this.isMobileDrawerOpen = false;
    });

    this.initLogos();
    this.loadSidebarWidth();
    // Track active theme so the toolbar toggle icon reflects light/dark state.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      this.isDarkTheme = this.themeService.currentThemeName === 'dark';
    });
    // Show/hide the toolbar "Save Theme" button based on pending preview
    // state — true if EITHER the theme/custom OR the custom layout is in a
    // previewed-but-not-saved state. A single Save commits whichever is pending.
    this.pendingPreviewSub = combineLatest([
      this.themeService.pendingPreview$,
      this.layoutService.pendingLayout$,
    ]).subscribe(([themePending, layoutPending]) => {
      this.hasPendingThemePreview = themePending || layoutPending;
    });
    // Mirror the slideshow service state into local fields so the toolbar
    // template can show controls (play/pause/prev/next/apply/save/stop) and
    // the progress bar without juggling multiple async pipes — the service
    // is a singleton, so subscribing once here is enough.
    this.slideshowStateSub = this.slideshowService.state$.subscribe((s) => {
      this.slideshowActive = s.active;
      this.slideshowPlaying = s.playing;
      this.slideshowIndex = s.index;
      this.slideshowTotal = s.items.length;
      this.slideshowCurrentName = s.current?.name || '';
    });
    this.slideshowProgressSub = this.slideshowService.progress$.subscribe((p) => {
      this.slideshowProgress = p;
    });
    this.dashboardTitleSub = this.dashboardTitleService.state$.subscribe((state) => {
      this.dashboardTitle = state.title;
      this.dashboardTitlePosition = state.position;
      this.dashboardTitleFontSize = state.fontSize;
    });
    this.publishedToolbarSub = this.publishedToolbar.state$.subscribe((state) => {
      this.publishedToolbarState = state;
    });
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();
      this.username =
        `${userDetails?.firstName} ${userDetails?.lastName}`.trim() || 'user';
      this.userEmail = userDetails?.email || '';
    }
    window.addEventListener('dvm:dashboard-published', this.onDashboardPublished);
    this.loadSidenavConfig();
  }
  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  static readonly DYNAMIC_DASHBOARD_STORAGE_KEY = 'dvm.dynamicDashboard';

  loadSidenavConfig() {
    // One-time cleanup: drop legacy UNCOMPRESSED dynamic-dashboard caches that
    // accumulated to ~10MB and exhausted the quota (breaking dashboard saves).
    // They re-cache (compressed, capped) as each dashboard is opened.
    purgeLegacyDynamicDashboardCaches();

    this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        this.menuItems = data;
        this.rebuildTopNavigationSections();
        this.loadDynamicDashboard();
      });
    this.loadTopNavigationConfig();
  }

  private loadTopNavigationConfig(): void {
    this.http
      .get<{ sections?: TopNavigationSectionConfig[] }>('assets/side-navbar-config/topnav.config.json')
      .subscribe({
        next: (config) => {
          this.topNavigationConfig = Array.isArray(config?.sections) ? config.sections : [];
          this.rebuildTopNavigationSections();
        },
        error: () => {
          this.topNavigationConfig = [];
          this.topNavigationSections = [];
        },
      });
  }

  private rebuildTopNavigationSections(): void {
    // The left sidenav is retired, so this top navigation is the ONLY menu on
    // screen — project mode must therefore drop just the builder
    // section, never the whole nav, or the published Dashboards list has nowhere
    // left to render.
    this.topNavigationSections = this.topNavigationConfig
      .filter((section) => !this.projectMode || !this.isBuilderSection(section))
      .map((section) => {
        // Every configured item is listed, including the screen you are on — it
        // gets marked instead (isTopNavigationItemActive). Dropping it used to
        // leave the menu one item short, and because a non-builder route has no
        // `view` param at all it hid Data Builder from dashboards too.
        const sourceItems = section.source === 'menuGroup'
          ? this.resolveTopNavigationMenuItems(section.groupName)
          : Array.isArray(section.items) ? section.items : [];
        return {
          ...section,
          resolvedItems: sourceItems.map((item) => this.resolveTopNavigationItem(item, section)),
        };
      });
  }

  /** True for a config section that only points at the Visualization Builder.
   *  Project mode hides every builder destination, so such a section would
   *  render as an empty dropdown — skip it entirely instead. */
  private isBuilderSection(section: TopNavigationSectionConfig): boolean {
    if (section.source !== 'static') return false;
    const items = Array.isArray(section.items) ? section.items : [];
    return items.length > 0
      && items.every((item) => String(item?.path ?? '').startsWith('/generate-visualization'));
  }

  private resolveTopNavigationMenuItems(groupName = ''): TopNavigationItem[] {
    const group = this.menuItems.find((item) => item?.name === groupName);
    const items = Array.isArray(group?.children) ? group.children : [];
    return items
      .filter((item: any) => item?.path)
      .map((item: any) => ({
        label: String(item.name || 'Dashboard'),
        description: String(item.description || 'Published analytical dashboard'),
        icon: String(item.icon || 'dashboard'),
        path: String(item.path),
        image: item.image || item.thumbnail || item.previewImage,
        accent: item.accent,
      }));
  }

  private resolveTopNavigationItem(
    item: TopNavigationItem,
    section: TopNavigationSectionConfig,
  ): TopNavigationItem {
    if (item.image || !section.thumbnailPattern) return { ...item };
    const slug = this.slugFromPath(item.path) || this.nameToSlug(item.label);
    return {
      ...item,
      image: section.thumbnailPattern.replace('{slug}', slug),
    };
  }

  /** True when this item is the dashboard already on screen. The list marks it
   *  "Viewing", so the panel answers "which one am I in?" as well as "where to
   *  next?" — the only state worth calling out in a product where you are
   *  always inside one dashboard. */
  isTopNavigationItemActive(path: string): boolean {
    const raw = String(path || '');
    if (!raw) return false;
    const currentUrl = this.currentUrl || this.router.url || '';
    if (currentUrl.split('?')[0] !== raw.split('?')[0]) return false;
    // Builder screens all live on /generate-visualization and differ only by
    // `?view=`, so the route alone doesn't identify them.
    const targetView = this.router.parseUrl(raw).queryParams['view'];
    if (!targetView) return true;
    const currentView = this.router.parseUrl(currentUrl).queryParams['view'] || 'data';
    return currentView === targetView;
  }

  openTopNavigation(sectionId: string): void {
    if (this.topNavigationCloseTimer) clearTimeout(this.topNavigationCloseTimer);
    this.activeTopNavigationId = sectionId;
  }

  scheduleTopNavigationClose(): void {
    if (this.topNavigationCloseTimer) clearTimeout(this.topNavigationCloseTimer);
    this.topNavigationCloseTimer = setTimeout(() => {
      this.activeTopNavigationId = '';
    }, 120);
  }

  closeTopNavigation(): void {
    if (this.topNavigationCloseTimer) clearTimeout(this.topNavigationCloseTimer);
    this.activeTopNavigationId = '';
  }

  useTopNavigationFallback(event: Event, fallback = ''): void {
    const image = event.target as HTMLImageElement;
    if (!fallback || image.dataset['fallbackApplied'] === 'true') return;
    image.dataset['fallbackApplied'] = 'true';
    image.src = fallback;
  }

  navigateTopNavigationItem(path: string): void {
    this.closeTopNavigation();
    const destination = this.router.parseUrl(path);
    const view = destination.queryParams['view'];
    void this.router.navigateByUrl(path).then(() => {
      if (view) {
        window.dispatchEvent(new CustomEvent('dvm:self-service-navigate', { detail: view }));
      }
    });
  }

  private getCurrentUserEmail(): string {
    if (this.userEmail) {
      return this.userEmail;
    }
    // Fallback for the rare case this runs before ngOnInit's loadUserProfile()
    // resolves — the JWT claim may be stale if the user's email was changed in
    // Keycloak since the token was issued, but it's better than nothing.
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed: any = keycloak?.tokenParsed ?? {};
    return String(tokenParsed?.email ?? tokenParsed?.preferred_username ?? 'anonymous');
  }
  private loadDynamicDashboard(): void {
    const env = environment as any;
    const base = ((env.SELF_SERVICE_API_URL || env.URL) as string | undefined)
      ?.replace(/\/$/, '') ?? '';
    if (!base) return;

    const payload = { email_id: [this.getCurrentUserEmail()] };

    this.http.post<any[]>(`${base}/dashboard_layouts/dynamic_filter`, payload).subscribe({
      next: (response) => {
        const list = Array.isArray(response) ? response : [];
        if (list.length === 0) return;

        // Each dashboard this user owns becomes a child of the "Published
        // Dashboards" group (NOT a flat top-level item) so every published
        // dashboard lives under one heading. We keep the API-backed
        // `/dynamic-dashboard/<slug>` route because it resolves for any user
        // (falls back to fetch-by-slug when localStorage is cold), unlike the
        // localStorage-only `/<slug>` route.
        const groupItems: Array<{ name: string; icon: string; path: string; isLandingPage?: boolean }> = [];

        list.forEach((item: any) => {
          const layoutJson = item?.layout_json;
          if (!layoutJson || typeof layoutJson !== 'object') return;

          // TWO different values, do not conflate them:
          //  · `dashboard_id` is the route identity — the name's slug PLUS a
          //    uniqueness suffix (e.g. "sales-margin-mse63tow3xh8"). The URL slug
          //    must come from this or the published view can't resolve it.
          //  · `layout_json['dashboard-name']` is what the creator typed. That is
          //    the only thing worth showing in the menu; labelling rows with the
          //    suffixed id put raw ids in front of the user.
          const dashboardId = String(item.dashboard_id || 'Dynamic Dashboard');
          const slug = this.nameToSlug(dashboardId);
          const name = String(
            (layoutJson as any)['dashboard-name'] ?? (layoutJson as any)['name'] ?? '',
          ).trim() || dashboardId;
          // Compressed + LRU-capped cache. Previously this wrote the full
          // layout_json UNCOMPRESSED, one key per owned dashboard, with no cap —
          // which grew to ~10MB and exhausted the localStorage quota, making
          // every dashboard/chart save fail. writeDynamicDashboardCache compresses
          // and evicts the oldest so the set can't blow the quota again.
          writeDynamicDashboardCache(slug, layoutJson);

          // This list is the email-filtered `dynamic_filter` result, so every
          // slug here is a dashboard the CURRENT user owns. Remember it so the
          // sidebar keeps the full nav (no lockdown) when the owner opens their
          // own dashboard — this survives logout because it's re-fetched from
          // the API on every login, unlike the localStorage `savedDashboards`
          // key which logout clears.
          this.ownedDashboardSlugs.add(slug);
          // Share ownership with the published-dashboard view (a different
          // component) via the root singleton service, so it can show
          // Back-to-Builder + Share for the creator — even for dashboards
          // published in a previous session (not in this browser's
          // localStorage). Logout-proof: re-registered on every login.
          this.selfServiceDashboardService.registerOwnedDashboardSlug(slug);
          // Recomputed ownership may flip the current route from locked-down to
          // full-menu, so drop the memo to force displayedMenuItems to re-run.
          this.displayedMenuCache = undefined;

          // Landing Page templates are ordinary dashboard_layouts rows flagged
          // by dashboardType — they get a home icon and are pinned FIRST in
          // the menu by applyPublishedDashboards.
          const isLandingPage = String((layoutJson as any)['dashboardType'] ?? '') === 'landing-page';
          groupItems.push({ name, icon: isLandingPage ? 'home' : 'dashboard', path: `/dvm-published/${slug}`, ...(isLandingPage ? { isLandingPage: true } : {}) });
        });

        if (groupItems.length > 0) {
          this.applyPublishedDashboards(groupItems);
        }
      },
      error: () => { /* API unavailable — no dynamic dashboard item shown */ },
    });
  }

  /** localStorage key the self-service builder saves published dashboards under. */
  private static readonly SAVED_DASHBOARDS_STORAGE_KEY = 'dvm.selfService.savedDashboards';

  private nameToSlug(name: string): string {
    return String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /** The bare slug for a menu path regardless of scheme — both `/<slug>` and
   *  `/dynamic-dashboard/<slug>` point at the same published dashboard, so we
   *  dedupe on this to avoid the same dashboard appearing twice. */
  private slugFromPath(path: string): string {
    return String(path || '')
      .replace(/^\/(?:dynamic-dashboard|dvm-published)\//, '')
      .replace(/^\//, '');
  }

  /** Merge freshly-loaded published dashboards into the single "Published
   *  Dashboards" group. Called by BOTH loaders (the select-all list and the
   *  per-user dynamic_filter list); each may arrive first, so we union into
   *  the group's existing children rather than replacing them, deduped by slug
   *  so the two path schemes for the same dashboard never double-list. */
  private applyPublishedDashboards(
    dashboardItems: Array<{ name: string; icon: string; path: string; isLandingPage?: boolean }>,
  ): void {
    if (dashboardItems.length === 0) return;

    // Pull any existing group children so a second loader adds to them instead
    // of clobbering the first loader's contribution. NOTE: the group is created
    // below with name 'Dashboards' — match THAT here (a 'Published Dashboards'
    // mismatch made this always miss, so a later loader/API response WIPED the
    // just-published entry a few seconds after it appeared).
    const existingGroup = this.menuItems.find((m) => m?.name === 'Dashboards' && Array.isArray(m?.children));
    const existingChildren: Array<{ name: string; icon: string; path: string; isLandingPage?: boolean }> =
      Array.isArray(existingGroup?.children) ? existingGroup!.children : [];

    // Union old + new, deduped by slug. The freshly-loaded item wins on a
    // collision (existing children keep their position; genuinely new
    // dashboards append) — `dashboardItems` is always the newer read, so this
    // lets an authoritative API name (e.g. `dynamic_filter`'s `dashboard-name`)
    // replace a stale/optimistic one (e.g. localStorage's placeholder
    // "Untitled dashboard") from an earlier loader instead of being shadowed
    // by it forever.
    const bySlug = new Map<string, { name: string; icon: string; path: string; isLandingPage?: boolean }>();
    const order: string[] = [];
    for (const item of existingChildren) {
      const slug = this.slugFromPath(item.path);
      if (!bySlug.has(slug)) order.push(slug);
      bySlug.set(slug, item);
    }
    for (const item of dashboardItems) {
      const slug = this.slugFromPath(item.path);
      if (!bySlug.has(slug)) order.push(slug);
      bySlug.set(slug, item);
    }
    // Landing Page templates always lead the menu (requirement: the landing
    // page is the FIRST entry). Stable partition — relative order within each
    // half stays first-seen-first.
    const mergedRaw = order.map((slug) => bySlug.get(slug)!);
    const mergedChildren = [
      ...mergedRaw.filter((c) => c.isLandingPage),
      ...mergedRaw.filter((c) => !c.isLandingPage),
    ];
    const mergedSlugs = new Set(mergedChildren.map((c) => this.slugFromPath(c.path)));

    // Remove any stray flat top-level items for dashboards now in the group —
    // legacy flat `/dynamic-dashboard/<slug>` entries and optimistic
    // `isPublishedDashboard` adds — so nothing shows outside the group.
    const cleaned = this.menuItems.filter(
      (m) =>
        m?.name !== 'Dashboards' &&
        !m?.isPublishedDashboard &&
        !(typeof m?.path === 'string' && mergedSlugs.has(this.slugFromPath(m.path))),
    );

    this.menuItems = [
      ...cleaned,
      { name: 'Dashboards', icon: 'dashboard_customize', children: mergedChildren },
    ];
    this.rebuildTopNavigationSections();
  }

  toggleSidebar() {
    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    this.scheduleDesktopReflow();
  }

  /**
   * Heavy dashboards (like all-charts) may instantiate/defer widgets around the
   * sidebar transition window. Fire staged reflows so late-mounted widgets also
   * recalculate dimensions after expand/collapse.
   */
  protected scheduleDesktopReflow(): void {
    // Immediate pass
    this.responsiveLayoutService.triggerReflow();

    // During transition
    setTimeout(() => this.responsiveLayoutService.triggerReflow(), 180);

    // After transition end
    setTimeout(() => {
      this.responsiveLayoutService.triggerReflow();
      window.dispatchEvent(new Event('resize'));
    }, 360);
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }
  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }
  goToHelp() {
    const helpUrl = environment.HELP_URL || 'https://default.help.com';
    window.open(helpUrl, '_blank');
  }

  goToFeedback() {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) window.open(feedbackUrl, '_blank');
  }

  showNotifications() {
    alert('Notifications clicked!');
  }

  openThemeSelector(): void {
    // Re-clicking the Theme Options button while the dialog is open used to
    // stack a second instance on top of the first. Guard against that by
    // tracking the live ref and bailing out if it's still open.
    if (this.themeSelectorDialogRef) {
      this.themeSelectorDialogRef.addPanelClass('dvm-theme-selector-pulse');
      return;
    }
    this.themeSelectorDialogRef = this.dialog.open(ThemeSelectorComponent, {
      panelClass: 'dvm-theme-selector-panel',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
    });
    this.themeSelectorDialogRef.afterClosed().subscribe(() => {
      this.themeSelectorDialogRef = null;
    });
  }

  toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  isActive(item: any): boolean {
    try {
      if (!item || !item.path) return false;
      return this.router.url.includes(item.path);
    } catch (e) {
      return false;
    }
  }

  canShowMacroShortcut(): boolean {
    return true;
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  selectMacro(macro: string): void {
    // Minimal selection handler — close panel and store selection
    this.isMacroPanelOpen = false;
    try {
      localStorage.setItem('selectedMacro', macro);
    } catch (e) {
      // ignore
    }
    console.log('Macro selected:', macro);
  }

  hasChildren(item: any): boolean {
    return item.children && item.children.length > 0;
  }
}


